import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { compose } from 'redux';
import { notification } from 'antd';
import { pending } from 'redux-saga-thunk';
import { change, reduxForm, reset } from 'redux-form';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';
import keyBy from 'lodash/keyBy';
import defaultTo from 'lodash/defaultTo';
import { fromEntities } from '../store/selectors';
import { modalHide, modalShow } from '../store/modal/actions';
import { isOpen } from '../store/modal/selectors';
import CreativeListModal from '../components/organisms/CreativeListModal';
import { createValidator, integer, minSize, required, url } from '../services/validation';
import {
  resourceCreateRequest,
  resourceDeleteRequest,
  resourceListReadRequest,
  resourceUpdateRequest,
  resourceUpdateSuccess,
} from '../store/resource/actions';
import { emptyEditRecord } from '../store/updateCampaign/actions';

class CreativeListModalContainer extends Component {
  state = {
    showAddCreative: false,
    showEdit: {},
  };

  handleMoveCreatives = children => {
    if (!Array.isArray(children)) return;

    const creativeList = keyBy(this.props.creativeList, 'creative_id');

    const re = [];
    children.forEach((creative, index) => {
      re.push({
        ...creativeList[creative],
        priority: index,
      });
    });

    re.forEach(creative => {
      this.props.change(`creative[${creative.creative_id}][priority]`, creative.priority);

      this.props.updateEntity(
        'advertiser-ad/:lineItemId/inhand/:product_id/creative/:id',
        creative,
        {
          data: creative,
          needle: { id: creative.creative_id },
        }
      );
    });
  };

  toggleAddCreative = () => {
    this.setState({ showAddCreative: !this.state.showAddCreative });
  };

  editing = (id, checked) => {
    this.setState({ showEdit: { ...this.state.showEdit, [id]: checked } });
  };

  render = () => [
    <CreativeListModal
      key={2}
      editing={this.editing}
      showAddCreative={this.state.showAddCreative}
      toggleAddCreative={this.toggleAddCreative}
      handleMoveCreatives={this.handleMoveCreatives}
      showEdit={this.state.showEdit}
      {...this.props}
    />,
  ];
}

const mapStateToProps = (
  { modal, ...state },
  {
    match: {
      params: { lineItemId },
    },
  }
) => {
  const list = groupBy(fromEntities.getList(state, 'advertiserAdInhandCreative'), 'product_id')[
    state.updateCampaign.product_id
  ];

  const defaults = {
    click_action: 'browser',
    creative_type: 'custom_url',
    width: 320,
    height: 503,
  };

  const creativeList = orderBy(list, 'priority').map(a => ({
    ...defaults,
    ...a,
    creative_url: a.url,
  }));
  const creative = keyBy(creativeList, 'creative_id');

  return {
    initialValues: { creative },
    lineItemId,
    ad: fromEntities.getDetail(state, 'advertiserAd', lineItemId),
    loading: pending(state, 'advertiserAdInhandCreativeListRead'),
    visibleCreativeModal: isOpen(modal, 'create-inhand-product-creative'),
    creativeList,
    activeProduct: get(state, 'updateCampaign', false),
  };
};

const mapDispatchToProps = (
  dispatch,
  {
    match: {
      params: { lineItemId },
    },
  }
) => ({
  change: (field, value) => dispatch(change('CreativeListModal', field, value)),
  updateEntity: (resource, detail, request) =>
    dispatch(resourceUpdateSuccess(resource, detail, request, {})),
  removeCreative: (productId, id) =>
    dispatch(
      resourceDeleteRequest('advertiser-ad/:lineItemId/inhand/:productId/creative/:id', {
        lineItemId,
        productId,
        id,
      })
    ),
  showCreativeModal: () => dispatch(modalShow('create-inhand-product-creative')),
  onCancelCreativeModal: () => {
    dispatch(modalHide('create-inhand-product-creative'));
    dispatch(reset('CreativeListModal'));

    // Timeout because hiding is a slow fade.
    // We don't want to flash the form resetting
    setTimeout(() => dispatch(emptyEditRecord()), 100);
  },
  updateCreative: ({ creative_id, product_id, ...data }) =>
    dispatch(
      resourceUpdateRequest(
        'advertiser-ad/:lineItemId/inhand/:product_id/creative/:id',
        { lineItemId, product_id, id: creative_id },
        {
          ...data,
          url: defaultTo(get(data, 'creative_url'), data.url),
          rate_type: defaultTo(get(data, 'rate_type'), 'daily'),
        }
      )
    ),
  addCreatives: (product_id, ids) =>
    dispatch(
      resourceCreateRequest(
        'advertiser-ad/:lineItemId/inhand/:product_id/creative',
        { lineItemId, product_id },
        { creative_service_id: ids.map(a => parseInt(a, 10)) }
      )
    ),
  getCreativeList: productId =>
    dispatch(
      resourceListReadRequest(
        'advertiser-ad/:lineItemId/inhand/:id/creative',
        { page_size: 100 },
        { lineItemId, id: productId }
      )
    ),
});

const onSubmit = (values, dispatch, props) => {
  if (!isEmpty(values.creative_service_id)) {
    const ids = values.creative_service_id;
    props
      .addCreatives(props.activeProduct.product_id, ids)
      .then(() => {
        notification.success({
          message: 'Success',
          description: 'Your creatives have been added.',
          duration: 4,
        });
      })
      .catch(e =>
        notification.error({
          message: 'Error',
          description: typeof e === 'string' ? e : 'Could not add creatives.',
          duration: 0,
        })
      );
  } else if (values.creative)
    return Promise.all(Object.values(values.creative).map(props.updateCreative))
      .then(() => {
        notification.success({
          message: 'Success',
          description: 'Your creatives have been updated.',
          duration: 4,
        });
      })
      .catch(e =>
        notification.error({
          message: 'Error',
          description: typeof e === 'string' ? e : 'Could not update creatives.',
          duration: 0,
        })
      );
};

const validate = createValidator({
  creative: createValidator({
    url: [url, required],
    budget: [integer, minSize(0)],
    width: [integer, required, minSize(0)],
    height: [integer, required, minSize(0)],
    impression_limit: [integer, minSize(0)],
    impression_interval: [integer, minSize(0)],
    creative_url: [required, url],
    creative_image: [required],
    creative_html: [required],
    click_through_url: [url],
  }),
});

export default compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  reduxForm({
    destroyOnUnmount: true,
    form: 'CreativeListModal',
    enableReinitialize: true,
    onSubmit,
    validate,
  })
)(CreativeListModalContainer);
